import { AxiosRequestConfig } from "axios";
import { callExternalApi } from "./external-api.service";
import { getLogger } from "src/utils/logger";

const logger = getLogger("slides.service");

const apiServerUrl = process.env.REACT_APP_API_SERVER_URL;

export interface GenerateSlidesRequest {
  docBase64: string;
  additionalInstructions?: string;
  templateName?: string;
  imageSource?: 'generative' | 'none';
}

export interface GenerateSlidesResponse {
  status: string;
  document_id: string;
  download_url: string;
  usage?: UsageInfo;
}

export interface UsageInfo {
  generative_used: number;
  generative_limit: number;
  generative_remaining: number;
  image_source_used: string;
  no_image_used: number;
  no_image_limit: number | null;
  no_image_remaining: number | null;
  can_use_no_image: boolean;
  total_generated: number;
  override_message?: string;
}

export interface UsageStatusResponse {
  usage: {
    generative_used: number;
    generative_limit: number;
    generative_remaining: number;
    no_image_used: number;
    no_image_limit: number | null;
    no_image_remaining: number | null;
    period: string;
    can_use_generative: boolean;
    can_use_no_image: boolean;
    total_generated: number;
  };
}

export async function generateSlides(
  body: GenerateSlidesRequest,
  accessToken: string
) {
  try {
    const config: AxiosRequestConfig = {
      url: `${apiServerUrl}/api/slides/generate`,
      method: "POST",
      headers: {
        "content-type": "application/json",
        Authorization: `Bearer ${accessToken}`,
      },
      data: body,
    };

    logger.info("Generating slides from uploaded document");

    const response = await callExternalApi({ config });

    if (response.error) {
      logger.error("Slides generation failed", response.error);
      return { 
        data: null,
        error: response.error 
      };
    }

    const data = response.data as unknown as GenerateSlidesResponse;
    logger.info("Slides generated successfully", { documentId: data.document_id });
    
    return {
      data: {
        documentId: data.document_id,
        downloadUrl: data.download_url,
        status: data.status,
        usage: data.usage
      },
      error: null
    };
  } catch (error) {
    logger.error("Unexpected error in generateSlides", error);
    return { 
      data: null,
      error: { message: (error as Error).message } 
    };
  }
}

export async function getSlideshowUsage(accessToken: string) {
  try {
    const config: AxiosRequestConfig = {
      url: `${apiServerUrl}/api/slides/usage`,
      method: "GET",
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    };

    logger.info("Fetching slideshow usage status");

    const response = await callExternalApi({ config });

    if (response.error) {
      logger.error("Failed to fetch usage status", response.error);
      return { 
        data: null,
        error: response.error 
      };
    }

    const data = response.data as any;
    logger.info("Usage status fetched successfully", data.usage);
    
    return { 
      data: data.usage,
      error: null 
    };
  } catch (error) {
    logger.error("Unexpected error in getSlideshowUsage", error);
    return { 
      data: null,
      error: { message: (error as Error).message } 
    };
  }
}

export async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      // If data URL, strip prefix
      const commaIndex = result.indexOf(",");
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.onerror = (e) => reject(e);
    reader.readAsDataURL(file);
  });
}
